import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEye,
  faUserCheck,
  faUserSlash,
  faBasketShopping,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import AddShopModal from "./shop/AddShopModal";
import SearchBar from "../../components/ui/SearchBar";
import TableWrapper from "../../components/ui/TableWrapper";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import DateFilter from "../../components/ui/DateFilter";
import { useShops } from "../../hooks";
import { toastSuccess, toastError } from "../../utils/toast";

const statusStyles = {
  ACTIVE: "bg-green-50 text-green-700 border-green-200",
  INACTIVE: "bg-red-50 text-red-700 border-red-200",
  active: "bg-green-50 text-green-700 border-green-200",
  inactive: "bg-red-50 text-red-700 border-red-200",
};

const statusDotStyles = {
  ACTIVE: "bg-green-500",
  INACTIVE: "bg-red-500",
  active: "bg-green-500",
  inactive: "bg-red-500",
};

const typeStyles = {
  Retail: "bg-blue-50 text-blue-700 border-blue-200",
  Wholesale: "bg-purple-50 text-purple-700 border-purple-200",
  Both: "bg-orange-50 text-orange-700 border-orange-200",
};

const ManageShop = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    shops,
    pagination,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    fetchShops,
    createShop,
    toggleShopStatus,
  } = useShops();

  const [showAddModal, setShowAddModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  useEffect(() => {
    const tab = searchParams.get("tab") || "all";
    setStatusFilter(tab);
  }, [searchParams, setStatusFilter]);

  const filteredShops = useMemo(() => {
    let result = shops;

    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((s) => {
        const createdDate = new Date(s.created_at);
        return createdDate >= start && createdDate <= end;
      });
    }

    return result;
  }, [shops, startDate, endDate, isFilterApplied]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    statusFilter,
    startDate,
    endDate,
    isFilterApplied,
    setCurrentPage,
  ]);

  const handleViewShop = (shop) => navigate(`/app/shops/${shop.shopId}`);

  const handleAddShop = async (newShop) => {
    try {
      await createShop(newShop);
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to add shop");
      throw error;
    }
  };

  const handleToggleStatus = async (shopId, currentStatus) => {
    try {
      await toggleShopStatus(shopId);
      toastSuccess(
        `Shop ${currentStatus === "active" || currentStatus === "ACTIVE" ? "deactivated" : "activated"} successfully`,
      );
      await fetchShops();
    } catch (error) {
      toastError(
        error?.response?.data?.message || "Failed to update shop status",
      );
    }
  };

  const tabs = [
    { id: "all", label: "All shops", icon: faBasketShopping },
    { id: "active", label: "Active", icon: faUserCheck },
    { id: "inactive", label: "Inactive", icon: faUserSlash },
  ];

  const tabCounts = {
    all: shops.length,
    active: shops.filter((s) => s.status === "active" || s.status === "ACTIVE")
      .length,
    inactive: shops.filter(
      (s) => s.status === "inactive" || s.status === "INACTIVE",
    ).length,
  };

  if (loading && shops.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-12 w-12 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading shops...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">
            Failed to load shops. Please try again.
          </p>
          <Button
            onClick={() => fetchShops()}
            className="mt-4 bg-blue-800 hover:bg-blue-900 text-white"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const displayShops = filteredShops;
  // `shops` contains only the current API page. Use Laravel's paginator
  // metadata so the controls can navigate through the complete result set.
  const totalItems = isFilterApplied
    ? displayShops.length
    : (pagination?.total ?? displayShops.length);
  const totalPages = isFilterApplied
    ? Math.max(1, Math.ceil(displayShops.length / pageSize))
    : (pagination?.last_page ?? Math.max(1, Math.ceil(totalItems / pageSize)));

  return (
    <div className="flex flex-col gap-6">
      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Manage shops
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Manage shops ({totalItems} total)
            </p>
          </div>
          <Button
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white shadow-sm"
            onClick={() => setShowAddModal(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add shop
          </Button>
        </div>
      </Card>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setCurrentPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setCurrentPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={totalItems}
        label="shops"
      />

      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-full lg:w-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex-1 lg:flex-none justify-center ${
                  statusFilter === tab.id
                    ? "bg-blue-800 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="h-3.5 w-3.5" />
                <span>{tab.label}</span>
                <span
                  className={`ml-0 sm:ml-1 px-2 py-0.5 text-xs rounded-full ${
                    statusFilter === tab.id
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {tabCounts[tab.id]}
                </span>
              </button>
            ))}
          </div>

          <SearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            placeholder="Search by shop name, location, or type..."
            className="w-full lg:w-80"
          />
        </div>
      </Card>

      <TableWrapper
        title="shops"
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <Table className="min-w-[800px]">
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                S/N
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Shop Name
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Location
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Type
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Status
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 text-center">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayShops.length > 0 ? (
              displayShops.map((shop, index) => {
                const startIndex = (currentPage - 1) * pageSize;
                const status = shop.status?.toUpperCase() || "ACTIVE";
                const type = shop.shop_type || shop.type || "Retail";

                return (
                  <TableRow
                    key={shop.shopId}
                    className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-100"
                  >
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {startIndex + index + 1}
                    </TableCell>
                    <TableCell className="py-3 px-4 font-medium text-gray-900 text-sm">
                      {shop.shop_name || shop.name}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {shop.location || "N/A"}
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${typeStyles[type] || "bg-gray-100 text-gray-700 border-gray-300"} border font-normal px-3 py-1 rounded-full text-xs`}
                      >
                        {type}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${statusStyles[status] || "bg-gray-100 text-gray-700 border-gray-300"} border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${statusDotStyles[status] || "bg-gray-400"}`}
                        />
                        {status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="flex gap-2 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewShop(shop)}
                          className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50"
                        >
                          <FontAwesomeIcon icon={faEye} className="h-3 w-3" />{" "}
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleToggleStatus(shop.shopId, shop.status)
                          }
                          className={`gap-1 ${
                            shop.status === "active" || shop.status === "ACTIVE"
                              ? "border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                              : "border-green-500 text-green-600 hover:bg-green-50"
                          }`}
                        >
                          {shop.status === "active" || shop.status === "ACTIVE"
                            ? "Deactivate"
                            : "Activate"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <EmptyState
                    title="No shops found"
                    message={
                      searchTerm || isFilterApplied
                        ? "Try adjusting your search or filters"
                        : statusFilter !== "all"
                          ? `No ${statusFilter} shops found`
                          : "Add your first shop to get started"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {displayShops.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      <AddShopModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
        }}
        onAdd={handleAddShop}
      />
    </div>
  );
};

export default ManageShop;
